import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import StateFallback from '../components/StateFallback';

const Memory = () => {
  const navigate = useNavigate();
  const { memories, loading, error, reload } = useApp();

  const active = memories.find((m) => m.status !== 'dismissed');
  const isConfirmed = active && active.status === 'confirmed';

  if (loading) {
    return <StateFallback variant="loading" />;
  }
  if (error) {
    return (
      <StateFallback
        variant="error"
        message="We couldn't load Milo's memories."
        onRetry={reload}
      />
    );
  }

  return (
    <div className="px-6 pt-8 pb-8 lp-enter">
      <div className="flex items-start justify-between">
        <h1
          className="font-serif-lp text-[40px] leading-none mb-2"
          style={{ color: 'var(--lp-cream)' }}
        >
          Memory
        </h1>
        <button
          onClick={() => navigate('/settings')}
          className="text-[16px] lp-pressable mt-1"
          style={{ color: 'var(--lp-cream)' }}
          data-testid="memory-settings-button"
        >
          Settings
        </button>
      </div>
      <p className="text-[15px] mb-7" style={{ color: 'var(--lp-body)' }}>
        {isConfirmed
          ? "Ask Lashachi about Milo's day, then review what happened today."
          : 'A provisional answer from Lashachi Pet Memory.'}
      </p>

      {!active && (
        <div className="lp-card p-5">
          <p style={{ color: 'var(--lp-body)' }}>No memories yet.</p>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* A02 — Provisional answer                                            */}
      {/* ------------------------------------------------------------------ */}
      {active && !isConfirmed && (
        <>
          <div className="lp-card p-5 mb-4">
            <h3
              className="font-serif-lp text-[20px] mb-2"
              style={{ color: 'var(--lp-cream)' }}
            >
              Ask Lashachi
            </h3>
            <p className="text-[15px]" style={{ color: 'var(--lp-body)' }}>
              {active.question}
            </p>
          </div>

          <div className="lp-card p-5 mb-4">
            <h3
              className="font-serif-lp text-[20px] mb-3"
              style={{ color: 'var(--lp-cream)' }}
            >
              Provisional answer
            </h3>
            <span className="lp-badge">Needs confirmation</span>
            <p
              className="text-[15px] leading-relaxed mt-3 mb-5"
              style={{ color: 'var(--lp-body)' }}
            >
              Possible outing — approximately {active.durationMinutes} minutes between{' '}
              {active.startTime} and {active.endTime}. Lashachi inferred this from two
              Ring motion observations; it needs your confirmation.
            </p>
            <button
              onClick={() => navigate(`/memory/${active.id}`)}
              className="lp-btn-sage w-full py-4 text-[16px]"
            >
              Review memory
            </button>
          </div>

          <div className="lp-card-dark p-5">
            <h3
              className="font-serif-lp text-[19px] mb-2"
              style={{ color: 'var(--lp-body)' }}
            >
              Why this answer is uncertain
            </h3>
            <p
              className="text-[13.5px] leading-relaxed"
              style={{ color: 'var(--lp-muted)' }}
            >
              {active.uncertainty}
            </p>
          </div>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* A06 — Confirmed Memory Overview                                     */}
      {/* ------------------------------------------------------------------ */}
      {active && isConfirmed && (
        <>
          <div className="lp-card-dark p-5 mb-7">
            <h3
              className="font-serif-lp text-[21px] mb-3"
              style={{ color: 'var(--lp-cream)' }}
            >
              Ask Lashachi
            </h3>
            <button
              onClick={() => navigate('/memory/ask')}
              className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl lp-pressable"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <span className="text-[15px]" style={{ color: 'var(--lp-body)' }}>
                {active.question}
              </span>
              <ArrowRight size={18} style={{ color: 'var(--lp-cream)' }} />
            </button>
          </div>

          <h3
            className="font-serif-lp text-[24px] mb-4"
            style={{ color: 'var(--lp-cream)' }}
          >
            Today
          </h3>

          <button
            onClick={() => navigate(`/memory/${active.id}`)}
            className="lp-card w-full text-left p-4 lp-pressable"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <span
                className="lp-badge whitespace-nowrap"
                style={{
                  background: 'rgba(214,230,222,0.16)',
                  color: 'var(--lp-sage)',
                  fontSize: '11px',
                  padding: '5px 10px',
                }}
              >
                {active.startTime}–{active.endTime}
              </span>
              <span
                className="text-[15px] font-semibold whitespace-nowrap"
                style={{ color: 'var(--lp-cream)' }}
              >
                {active.confirmedTitle}
              </span>
              <span
                className="text-[13px] ml-auto whitespace-nowrap"
                style={{ color: 'var(--lp-body)' }}
              >
                {active.duration}
              </span>
            </div>
            <span className="lp-badge">Confirmed</span>
          </button>
        </>
      )}
    </div>
  );
};

export default Memory;
